import { dedupeFilenames, sanitizeFilenameBase } from '@/domain/files/filename';
import { IMenuQrCopy } from './menu-qr-copy';
import {
  IMenuQrOrderingContext,
  resolveMenuQrDesign,
  TMenuQrDesign,
  TMenuQrDesignMode,
  TMenuQrTargetKind,
} from './menu-qr-design';
import { buildMenuQrUrl } from './menu-qr-url';

export interface IMenuQrSelection {
  includeStorefront: boolean;
  includeTakeAway: boolean;
  includeTables: boolean;
  selectedTableIds: number[];
}

export interface IMenuQrTableLike {
  id: number;
  name: string;
  token?: string | null;
}

/** One poster/sticker to generate, in download order. */
export interface IMenuQrTarget {
  id: string;
  kind: TMenuQrTargetKind;
  url: string;
  design: TMenuQrDesign;
  /** Text drawn under the QR; null when the design draws no title. */
  title: string | null;
  /** Human label for the preview caption. */
  caption: string;
  /** Path inside the downloaded zip. */
  zipPath: string;
}

export interface IMenuQrTargetNames {
  storefrontCaption: string;
  takeAwayCaption: string;
  genericFile: string;
  takeAwayFile: string;
  tablesFolder: string;
}

export interface IMenuQrTargetsInput {
  restaurantId: number;
  branchToken: string;
  tables: IMenuQrTableLike[];
  selection: IMenuQrSelection;
  mode: TMenuQrDesignMode;
  ordering: IMenuQrOrderingContext;
  copy: IMenuQrCopy;
  names: IMenuQrTargetNames;
}

export interface IMenuQrTargetsResult {
  targets: IMenuQrTarget[];
  /** Names of selected tables that have no token yet (excluded from targets). */
  skippedTables: string[];
}

export function buildMenuQrTargets(
  input: IMenuQrTargetsInput
): IMenuQrTargetsResult {
  const { selection, ordering, copy, names } = input;
  const targets: IMenuQrTarget[] = [];
  const skippedTables: string[] = [];
  const isTierZero = ordering.effectiveTier <= 0;
  const designFor = (kind: TMenuQrTargetKind) =>
    resolveMenuQrDesign(input.mode, kind, ordering);

  // Without an ordering plan the storefront QR is the only one that exists.
  if (isTierZero || selection.includeStorefront) {
    const design = designFor('storefront');

    targets.push({
      id: 'storefront',
      kind: 'storefront',
      url: buildMenuQrUrl({
        restaurantId: input.restaurantId,
        branchToken: input.branchToken,
      }),
      design,
      title: design === 'orders' ? copy.genericTitle : null,
      caption: names.storefrontCaption,
      zipPath: `${fileNameBase(names.genericFile, 'generic')}.png`,
    });
  }

  if (!isTierZero && selection.includeTakeAway) {
    const design = designFor('takeaway');

    targets.push({
      id: 'takeaway',
      kind: 'takeaway',
      url: buildMenuQrUrl({
        restaurantId: input.restaurantId,
        branchToken: input.branchToken,
        takeAway: true,
      }),
      design,
      title: design === 'orders' ? copy.takeAwayTitle : null,
      caption: names.takeAwayCaption,
      zipPath: `${fileNameBase(names.takeAwayFile, 'takeaway')}.png`,
    });
  }

  if (!isTierZero && selection.includeTables) {
    const design = designFor('table');
    const folder = fileNameBase(names.tablesFolder, 'tables');
    const selectedTables = input.tables.filter((table) =>
      selection.selectedTableIds.includes(table.id)
    );
    const printableTables: IMenuQrTableLike[] = [];

    for (const table of selectedTables) {
      if (table.token) {
        printableTables.push(table);
      } else {
        skippedTables.push(table.name);
      }
    }

    const fileNames = dedupeFilenames(
      printableTables.map((table) =>
        fileNameBase(table.name, `table-${table.id}`)
      )
    );

    printableTables.forEach((table, index) => {
      targets.push({
        id: `table:${table.id}`,
        kind: 'table',
        url: buildMenuQrUrl({
          restaurantId: input.restaurantId,
          tableToken: table.token,
        }),
        design,
        title: table.name,
        caption: table.name,
        zipPath: `${folder}/${fileNames[index]}.png`,
      });
    });
  }

  return { targets, skippedTables };
}

/** File-name base with invalid characters removed and repeated spaces collapsed. */
function fileNameBase(input: string, fallback: string): string {
  return sanitizeFilenameBase(input, fallback).replace(/ {2,}/g, ' ');
}

export function buildMenuQrZipName(
  restaurantName: string,
  branchName: string
): string {
  const branchSuffix = branchName === 'Principal' ? '' : ` ${branchName}`;

  return `${fileNameBase(`QR ${restaurantName}${branchSuffix}`, 'QR')}.zip`;
}
